import { useEffect, useState } from "react";

import apiService from "../../../../services/apiService";
import { IStation } from "../../../../models/IUser";

/**
 * `R.7` (`ТЕСТ-Н109`): итог переключения опроса, пока окно модуля открыто.
 *
 * После `PATCH /station/:id/polling` изделие подтверждает смену не сразу:
 * сервер отвечает `accepted`, а применённой она становится после
 * `Configuration` с `match`. Поэтому окно:
 *
 * - сразу ставит «Применяется…» и перечитывает строку **раз в 2 с**;
 * - снимает ожидание по `polling_applied === true` — и берёт `polling_enabled`
 *   из ответа, а не из нажатия: «выкл» показывается только тогда, когда изделие
 *   это подтвердило;
 * - через **30 с** без подтверждения пишет «не подтвердило» и возвращает
 *   переключатель — ждать бесконечно нельзя;
 * - при закрытии окна опрос останавливается: `clearInterval` в уборке эффекта.
 *
 * Вынесено из `PopupModule`, чтобы это поведение проверялось без отрисовки
 * всей формы: таймеры и подмена API здесь и есть предмет проверки.
 */
export const POLLING_CONFIRM_TIMEOUT_MS = 30_000;
export const POLLING_POLL_INTERVAL_MS = 2_000;

export type PollingOverride = {
  state: "pending" | "applied" | "timeout";
  enabled: boolean;
  startedAt: number;
};

export function usePollingConfirmation() {
  const [pollingOverrides, setPollingOverrides] = useState<
    Record<number, PollingOverride>
  >({});

  useEffect(() => {
    const pending = Object.entries(pollingOverrides).filter(
      ([, value]) => value.state === "pending",
    );
    if (pending.length === 0) return;

    let cancelled = false;

    const tick = async () => {
      for (const [key, override] of pending) {
        const id = Number(key);

        if (Date.now() - override.startedAt >= POLLING_CONFIRM_TIMEOUT_MS) {
          if (!cancelled) {
            setPollingOverrides((prev) => ({
              ...prev,
              [id]: { ...prev[id], state: "timeout" },
            }));
          }
          continue;
        }

        try {
          const { data } = await apiService.fetchEntity<IStation>(
            `/station/${id}`,
          );
          const row = Array.isArray(data) ? data[0] : data;

          if (row?.polling_applied === true && !cancelled) {
            setPollingOverrides((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                state: "applied",
                enabled: row.polling_enabled !== false,
              },
            }));
          }
        } catch {
          // Связь пропала — ждём до 30 с, затем скажем «не подтвердило».
        }
      }
    };

    const timer = setInterval(tick, POLLING_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [pollingOverrides]);

  const markPollingPending = (id: number, enabled: boolean) => {
    setPollingOverrides((prev) => ({
      ...prev,
      [id]: { state: "pending", enabled, startedAt: Date.now() },
    }));
  };

  return { pollingOverrides, markPollingPending };
}
